import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
    selector: '[hamTooltip]',
})
export class HamTooltipDirective implements OnDestroy {
    @Input('hamTooltip') tooltipText!: string;
    @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' | 'top-right' = 'top';
    @Input() backGroundHexColor: string;
    @Input() withArrow: boolean = false;
    @Input() tooltipClass: string | undefined;
    @Input() multiline: boolean = false; // New multiline option
    /** Max width of a multiline tooltip; wider for a short sentence that should break only where its text says. */
    @Input() tooltipMaxWidth: string = '200px';
    /** Show only when the host's own text is cut off (ellipsis), e.g. a long file name in a narrow cell. */
    @Input() tooltipOnlyWhenTruncated: boolean = false;
    /** Puts the tooltip above modals (z-index 1200 instead of 1000), e.g. for a cut-off name inside a ham-modal. */
    @Input() tooltipAboveModals: boolean = false;

    private tooltipElement!: HTMLElement;

    /** Breathing room kept between the tooltip and the edge of the viewport when it has to be pulled in. */
    private static readonly VIEWPORT_MARGIN_PX = 8;

    constructor(private el: ElementRef, private renderer: Renderer2) { }

    @HostListener('mouseenter') onMouseEnter() {
        if (!this.tooltipText) return;
        if (this.tooltipOnlyWhenTruncated && !this.isTruncated()) return;

        this.createTooltip();
        this.setPosition();
        this.renderer.setStyle(this.tooltipElement, 'visibility', 'visible');
        this.renderer.setStyle(this.tooltipElement, 'opacity', '1');
    }

    @HostListener('mouseleave') onMouseLeave() {
        this.destroyTooltip();
    }

    ngOnDestroy(): void {
        this.destroyTooltip();
    }

    private createTooltip() {
        this.destroyTooltip();
        // Create tooltip element
        this.tooltipElement = this.renderer.createElement('div');
        this.renderer.addClass(this.tooltipElement, 'app-tooltip');
        this.renderer.setAttribute(this.tooltipElement, 'data-testid', 'ham-tooltip');
        if (this.tooltipClass) { this.renderer.addClass(this.tooltipElement, this.tooltipClass); }
        this.renderer.appendChild(this.tooltipElement, this.renderer.createText(this.tooltipText));

        // Append tooltip to the body
        this.renderer.appendChild(document.body, this.tooltipElement);

        // Initial styles
        this.renderer.setStyle(this.tooltipElement, 'position', 'absolute');
        this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
        this.renderer.setStyle(this.tooltipElement, 'opacity', '0');
        this.renderer.setStyle(this.tooltipElement, 'transition', 'opacity 0.2s');
        this.renderer.setStyle(this.tooltipElement, 'z-index', this.tooltipAboveModals ? '1200' : '1000');

        if (this.backGroundHexColor) {
            this.renderer.setStyle(this.tooltipElement, 'background-color', this.backGroundHexColor);
        }

        if (this.withArrow) {
            this.renderer.addClass(this.tooltipElement, 'with-arrow');
        }

        if (this.multiline) {
            this.renderer.setStyle(this.tooltipElement, 'max-width', this.tooltipMaxWidth);
            this.renderer.setStyle(this.tooltipElement, 'white-space', 'pre-line');
            this.renderer.setStyle(this.tooltipElement, 'word-break', 'break-word');
        } else {
            this.renderer.setStyle(this.tooltipElement, 'white-space', 'nowrap');
        }

        this.renderer.setAttribute(this.tooltipElement, 'data-position', this.tooltipPosition);
    }

    private setPosition() {
        const hostRect = this.el.nativeElement.getBoundingClientRect();
        const tooltipRect = this.tooltipElement.getBoundingClientRect();
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        const scrollX = window.scrollX || document.documentElement.scrollLeft;

        let top = 0;
        let left = 0;

        switch (this.tooltipPosition) {
            case 'top':
                top = hostRect.top + scrollY - tooltipRect.height - 8; // 8px gap
                left = hostRect.left + scrollX + (hostRect.width - tooltipRect.width) / 2;
                break;
            case 'top-right':
                top = hostRect.top + scrollY - tooltipRect.height - 8; // 8px gap
                left = hostRect.right + scrollX - tooltipRect.width; // align to right edge
                break;
            case 'bottom':
                top = hostRect.bottom + scrollY + 8; // 8px gap
                left = hostRect.left + scrollX + (hostRect.width - tooltipRect.width) / 2;
                break;
            case 'left':
                top = hostRect.top + scrollY + (hostRect.height - tooltipRect.height) / 2;
                left = hostRect.left + scrollX - tooltipRect.width - 8; // 8px gap
                break;
            case 'right':
                top = hostRect.top + scrollY + (hostRect.height - tooltipRect.height) / 2;
                left = hostRect.right + scrollX + 8; // 8px gap
                break;
        }

        // Keep it inside the viewport. Everything above positions the tooltip relative to its host
        // and can happily land off-screen; this is the only thing stopping that.
        //
        // ⚠️ NOT cosmetic. The tooltip is appended to <body> as `position: absolute` with
        // `white-space: nowrap`, so one that runs past the right edge EXPANDS THE DOCUMENT and raises
        // a horizontal scrollbar. The scrollbar reflows the page, the reflow moves the host out from
        // under the pointer, `mouseleave` tears the tooltip down, the scrollbar disappears, the layout
        // springs back under the pointer and `mouseenter` fires again — a visible flicker loop at
        // pointer speed, for as long as the user hovers. It shows up on any tooltip in a right-hand
        // table column with a long label (found on the ABCP documents hub's row action, whose German
        // label is far wider than its icon button).
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;
        const margin = HamTooltipDirective.VIEWPORT_MARGIN_PX;

        // `Math.max` last so a tooltip wider than the viewport pins to the left edge instead of being
        // pushed off the other side by its own clamp.
        left = Math.max(
            scrollX + margin,
            Math.min(left, scrollX + viewportWidth - tooltipRect.width - margin),
        );

        // Vertically, FLIP rather than clamp: sliding a 'top' tooltip down would park it on top of the
        // element it describes, so it goes to the other side of the host instead.
        if (top < scrollY + margin) {
            top = hostRect.bottom + scrollY + margin;
        } else if (top + tooltipRect.height > scrollY + viewportHeight - margin) {
            top = Math.max(scrollY + margin, hostRect.top + scrollY - tooltipRect.height - margin);
        }

        // Apply calculated positions
        this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
        this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
    }

    /** The host clips its own content. Height gets 1px slack: glyph descenders can overhang the line box. */
    private isTruncated(): boolean {
        const host: HTMLElement = this.el.nativeElement;
        return host.scrollWidth > host.clientWidth || host.scrollHeight > host.clientHeight + 1;
    }

    private destroyTooltip() {
        if (this.tooltipElement) {
            this.renderer.removeChild(document.body, this.tooltipElement);
            this.tooltipElement = null!;
        }
    }
}
